import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isBefore } from 'date-fns';
import { normalize, schema } from 'normalizr';
import { combineReducers } from 'redux';
import { StockDto, WarehouseDto } from '../../../common/PricePrgClient';
import { createAsyncAction, toDateString } from '../../../utils';
import { DateStock, Stocks } from '../stockTypes';

const namespaces = {
    stocks: 'stocks',
    warehouses: 'warehouses'
};

type FetchStocksForDayPayload = {
    date: string;
    warehouseId: number;
};

function updateStockState(stock: StockDto, dateStocks: DateStock[]) {
    const newDateStock: DateStock = {
        date: toDateString(stock.date),
        amount: stock.amount
    };

    for (let i = 0; i < dateStocks.length; i++) {
        const existingDateStr = dateStocks[i].date;

        if (newDateStock.date === existingDateStr) {
            dateStocks[i].amount = stock.amount;
            return;
        } else if (isBefore(new Date(newDateStock.date), new Date(existingDateStr))) {
            dateStocks.splice(i, 0, newDateStock);
            return;
        }
    }

    dateStocks.push(newDateStock);
}

export const fetchStocksForDay = createAsyncAction<FetchStocksForDayPayload, StockDto[]>(
    namespaces.stocks,
    'fetchStocksForDay'
);

const stockSlice = createSlice({
    name: namespaces.stocks,
    initialState: {} as Stocks,
    reducers: {
        updateStock: (
            state,
            action: PayloadAction<{ updatedStock: StockDto[]; deletedStock: StockDto[] }>
        ) => {
            const { payload } = action;
            for (const deleted of payload.deletedStock) {
                const warehouseGrp = state[deleted.warehouseId];
                if (warehouseGrp != null) {
                    const productGrp = warehouseGrp[deleted.productId];
                    if (productGrp != null) {
                        const dateStrToRemove = toDateString(deleted.date);
                        const idxToRemove = productGrp.findIndex(x => x.date === dateStrToRemove);
                        if (idxToRemove !== -1) {
                            productGrp.splice(idxToRemove, 1);
                        }
                    }
                }
            }

            for (const updated of payload.updatedStock) {
                const warehouseGrp = state[updated.warehouseId];
                if (warehouseGrp != null) {
                    const productGrp = warehouseGrp[updated.productId];
                    if (productGrp != null) {
                        updateStockState(updated, productGrp);
                    }
                }
            }
        }
    },
    extraReducers: builder =>
        builder.addCase(fetchStocksForDay.success, (state, action) => {
            const { payload } = action;

            for (const stock of payload) {
                const warehouseGrp = (state[stock.warehouseId] = state[stock.warehouseId] || {});
                const productGrp = (warehouseGrp[stock.productId] = warehouseGrp[stock.productId] || []);

                updateStockState(stock, productGrp);
            }
        })
});
export const { updateStock } = stockSlice.actions;

const warehouseEntity = new schema.Entity<WarehouseDto>('warehouses', undefined, {
    idAttribute: 'warehouseId'
});

export const fetchWarehouses = createAsyncAction<void, WarehouseDto[]>(
    namespaces.warehouses,
    'fetchWarehouses'
);
export const updateWarehouse = createAsyncAction<WarehouseDto, WarehouseDto>(
    namespaces.warehouses,
    'updateWarehouse'
);

const warehouseSlice = createSlice({
    name: namespaces.warehouses,
    initialState: {} as { [id: number]: WarehouseDto },
    reducers: {},
    extraReducers: builder =>
        builder
            .addCase(fetchWarehouses.success, (state, action) => {
                const normalized = normalize(action.payload, [warehouseEntity]);
                return normalized.entities.warehouses ?? {};
            })
            .addCase(updateWarehouse.success, (state, action) => {
                if (action.payload.isPrimaryWarehouse) {
                    for (const key in state) {
                        if (Object.prototype.hasOwnProperty.call(state, key)) {
                            const warehouse = state[key];
                            if (
                                warehouse.warehouseId !== action.payload.warehouseId &&
                                warehouse.isPrimaryWarehouse
                            ) {
                                warehouse.isPrimaryWarehouse = false;
                            }
                        }
                    }
                }

                state[action.payload.warehouseId] = action.payload;
            })
});

export default combineReducers({
    stock: stockSlice.reducer,
    warehouses: warehouseSlice.reducer
});
